import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Notification, NotificationStatus } from '../types'
import { NotificationsUiContext, type ModerationIssue } from './NotificationsUiContext'

export function NotificationsUiProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [notifications, setNotificationsState] = useState<Notification[]>([])
  const [moderationIssues, setModerationIssues] = useState<Record<number, ModerationIssue>>({})

  const setModerationIssue = useCallback((id: number, issue: ModerationIssue) => {
    setModerationIssues((prev) => (prev[id] === issue ? prev : { ...prev, [id]: issue }))
  }, [])

  // After approve/decline, the backend can keep echoing the same notification
  // through the WS `latest_notifications` snapshot for a few seconds. Without
  // this guard, the badge count bounces back up the moment a new snapshot
  // arrives. Keep dismissed IDs in a ref so they survive across WS pushes.
  const dismissedIds = useRef<Set<number>>(new Set())

  // Resolved (approved/declined) requests keep showing a status badge instead of
  // vanishing. Remembered in a ref so the outcome survives — and re-applies to —
  // every incoming WS snapshot, which would otherwise revert the card to its
  // original "actionable" state.
  const resolvedStatuses = useRef<Map<number, NotificationStatus>>(new Map())

  const applyStatus = useCallback((n: Notification): Notification => {
    const status = resolvedStatuses.current.get(n.id)
    return status ? { ...n, localStatus: status, actionable: false } : n
  }, [])

  const setNotifications = useCallback(
    (list: Notification[]) => {
      const incoming = list.filter((n) => !dismissedIds.current.has(n.id)).map(applyStatus)
      const incomingIds = new Set(incoming.map((n) => n.id))
      setNotificationsState((prev) => {
        // Keep resolved cards the snapshot no longer includes so their outcome
        // stays on screen until the panel is reopened / the page reloads.
        const retained = prev.filter((n) => n.localStatus && !incomingIds.has(n.id))
        return [...incoming, ...retained]
      })
    },
    [applyStatus]
  )

  const addNotification = useCallback(
    (n: Notification) => {
      if (dismissedIds.current.has(n.id)) return
      const item = applyStatus(n)
      setNotificationsState((prev) =>
        prev.some((x) => x.id === n.id)
          ? prev.map((x) => (x.id === n.id ? item : x))
          : [item, ...prev]
      )
    },
    [applyStatus]
  )

  const clearModerationIssue = useCallback((id: number) => {
    setModerationIssues((prev) => {
      if (!(id in prev)) return prev
      const rest = { ...prev }
      delete rest[id]
      return rest
    })
  }, [])

  const removeNotification = useCallback(
    (id: number) => {
      dismissedIds.current.add(id)
      clearModerationIssue(id)
      setNotificationsState((prev) => prev.filter((n) => n.id !== id))
    },
    [clearModerationIssue]
  )

  const resolveNotification = useCallback(
    (id: number, status: NotificationStatus) => {
      resolvedStatuses.current.set(id, status)
      clearModerationIssue(id)
      setNotificationsState((prev) =>
        prev.map((n) => (n.id === id ? { ...n, localStatus: status, actionable: false } : n))
      )
    },
    [clearModerationIssue]
  )

  const value = useMemo(
    () => ({
      open,
      setOpen,
      notifications,
      setNotifications,
      addNotification,
      removeNotification,
      resolveNotification,
      moderationIssues,
      setModerationIssue,
    }),
    [
      open,
      notifications,
      setNotifications,
      addNotification,
      removeNotification,
      resolveNotification,
      moderationIssues,
      setModerationIssue,
    ]
  )

  return <NotificationsUiContext.Provider value={value}>{children}</NotificationsUiContext.Provider>
}
